/** Compact HubSpot summary card (Demo Mode only, conceptual CRM integration - see
 * `mocks/data/customerManagementData.ts`). Shown alongside the existing summary/metric tiles on
 * a client's overview page (`CustomerWorkspace`/`OnboardingDetail`) - deliberately small, never a
 * dedicated integration page. All actions are mocked (no real HubSpot API call, authentication
 * or background sync). */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Autorenew, InfoOutlined, OpenInNew } from '@mui/icons-material';
import { Section } from '../../../components/ui/Section';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import HubSpotIndicator from '../../../components/shared/crm/HubSpotIndicator';
import {
  CRM_STATUS_COLOR, CRM_STATUS_LABEL, CustomerRecord, formatHubSpotLastSynced,
  getClientCrmDetails, linkClientToHubSpot, syncClientWithHubSpot,
} from '../../../mocks/data/customerManagementData';

interface HubSpotSummaryCardProps {
  customer: CustomerRecord;
  /** Called after any mock mutation so the parent page can force a re-render and pick up the
   * freshly-updated record (same "mutate + refresh" pattern already used elsewhere in Customer
   * Management, e.g. `OnboardingDetail`'s milestone toggles). */
  onUpdated: () => void;
}

// lazy: dash-cased CustomerCrmStatus values aren't valid camelCase key fragments, so map
// them explicitly to the translation keys rather than concatenating the raw status.
const CRM_STATUS_KEY: Record<string, string> = {
  linked: 'crmStatusLinked',
  'not-linked': 'crmStatusNotLinked',
  'sync-overdue': 'crmStatusSyncOverdue',
  'attention-required': 'crmStatusAttentionRequired',
};

const HubSpotSummaryCard = ({ customer, onUpdated }: HubSpotSummaryCardProps) => {
  const { t } = useTranslation('common');
  const crm = getClientCrmDetails(customer);
  const [syncing, setSyncing] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addValue, setAddValue] = useState('');
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const runMockSync = () => {
    setSyncing(true);
    window.setTimeout(() => {
      syncClientWithHubSpot(customer.id);
      setSyncing(false);
      onUpdated();
      setToast({ open: true, message: t('hubspotSyncSuccess', 'HubSpot information updated.') });
    }, 700);
  };

  const handleConfirmAdd = () => {
    if (!addValue.trim()) return;
    linkClientToHubSpot(customer.id, addValue);
    setAddDialogOpen(false);
    onUpdated();
    setToast({ open: true, message: t('hubspotLinkSuccess', 'Client linked to HubSpot.') });
  };

  return (
    <Section style={{ p: 4, mb: 0, height: '100%' }}>
      <Stack spacing={1.5}>
        <Stack direction="row"
          alignItems="flex-start"
          justifyContent="space-between">
          <Stack direction="row"
            spacing={1}
            alignItems="center">
            <HubSpotIndicator />
            <Typography variant="textSM"
              fontWeight={600}>{t('hubspotCardTitle', 'HubSpot')}</Typography>
          </Stack>
          <Chip
            label={t(CRM_STATUS_KEY[crm.status], CRM_STATUS_LABEL[crm.status])}
            size="small"
            color={CRM_STATUS_COLOR[crm.status]}
            variant={crm.status === 'not-linked' ? 'outlined' : 'filled'}
          />
        </Stack>

        {crm.status === 'not-linked' && (
          <>
            <Typography variant="textSM"
              color="text.secondary">
              {t('hubspotCardNotLinkedHelp', 'Add a HubSpot company ID to link this client.')}
            </Typography>
            <Box>
              <Button variant="secondary"
                size="lg"
                onClick={() => { setAddValue(''); setAddDialogOpen(true); }}>
                {t('hubspotCardAddId', 'Add HubSpot ID')}
              </Button>
            </Box>
          </>
        )}

        {crm.status !== 'not-linked' && (
          <>
            <Box>
              <Typography variant="textXS"
                color="text.secondary">{t('hubspotCardCompanyId', 'Company ID')}</Typography>
              <Typography variant="textSM"
                fontWeight={500}>{crm.companyId}</Typography>
            </Box>
            <Box>
              <Typography variant="textXS"
                color="text.secondary">{t('hubspotCardLastSync', 'Last synchronised')}</Typography>
              <Typography variant="textSM">{formatHubSpotLastSynced(crm.lastSyncAt)}</Typography>
            </Box>

            {crm.status === 'attention-required' && (
              <Stack direction="row"
                spacing={0.5}
                alignItems="center">
                <Typography variant="textSM"
                  color="error">
                  {t('hubspotCardAttentionHelp', 'The last sync was unsuccessful.')}
                </Typography>
                {crm.lastSyncError && (
                  <Tooltip title={crm.lastSyncError}>
                    <InfoOutlined fontSize="small"
                      color="disabled"
                      aria-label={t('hubspotCardErrorDetail', 'Sync error detail')} />
                  </Tooltip>
                )}
              </Stack>
            )}
            {crm.status === 'sync-overdue' && (
              <Typography variant="textSM"
                color="warning.main">
                {t('hubspotCardOverdueHelp', 'This client hasn\u2019t synchronised with HubSpot recently.')}
              </Typography>
            )}

            <Stack direction="row"
              spacing={1}
              alignItems="center">
              {crm.status === 'attention-required' ? (
                <Button
                  variant="primary"
                  size="lg"
                  disabled={syncing}
                  startIcon={syncing ? <CircularProgress size={14} /> : undefined}
                  onClick={runMockSync}
                >
                  {t('hubspotCardRetry', 'Retry')}
                </Button>
              ) : (
                <>
                  <Button
                    variant="secondary"
                    size="lg"
                    endIcon={<OpenInNew fontSize="small" />}
                    onClick={() => window.open(crm.companyUrl ?? '', '_blank', 'noopener,noreferrer')}
                  >
                    {t('hubspotCardOpen', 'Open in HubSpot')}
                  </Button>
                  <Tooltip title={t('hubspotCardSyncTooltip', 'Sync HubSpot information')}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('hubspotCardSyncTooltip', 'Sync HubSpot information')}
                        onClick={runMockSync}
                        disabled={syncing}
                      >
                        {syncing ? <CircularProgress size={16} /> : <Autorenew fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          </>
        )}
      </Stack>

      <Dialog open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogTitle>{t('hubspotCardAddId', 'Add HubSpot ID')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            label={t('hubspotCompanyId', 'HubSpot company ID')}
            value={addValue}
            onChange={(e) => setAddValue(e.target.value)}
            helperText={t('hubspotCompanyIdHelper', 'Enter the HubSpot company record ID to link this client to its CRM record.')}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={() => setAddDialogOpen(false)}>{t('cancel', 'Cancel')}</Button>
          <Button variant="primary"
            disabled={!addValue.trim()}
            onClick={handleConfirmAdd}>{t('save', 'Save')}</Button>
        </DialogActions>
      </Dialog>

      <MessagesSnackbar
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity="success"
        duration={3000}
      />
    </Section>
  );
};

export default HubSpotSummaryCard;
